import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Platform, ScrollView, StyleSheet, View, type ViewStyle } from "react-native";
import type { GetModernizedCoursePublishHistoryQuery } from "@axio/graphql/generated/graphql";

import PublishHistorySheet from "./components/PublishHistorySheet";
import ExpandedReviewContextBar from "./components/ExpandedReviewContextBar";
import PublishSheet from "./components/PublishSheet";
import ReviewActionPanel from "./components/ReviewActionPanel";
import ReviewContentPanel from "./components/ReviewContentPanel";
import ReviewSidebar from "./components/ReviewSidebar";
import ReviewTopBar from "./components/ReviewTopBar";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "./constants/itemTypeLabels";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
} from "./types/reviewTypes";
import type {
  ActivityDetail,
  PublishedPublishItem,
  PublishDocumentUpdate,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
  StructuredFields,
} from "./types/reviewTypes";
import { colors } from "../../../../styles/design-system";

type PublishHistoryRecord =
  GetModernizedCoursePublishHistoryQuery["getModernizedCoursePublishHistory"]["records"][number];

function getExpandedItemTypeLabel(item: CourseEvaluatorReviewSidebarItem): string {
  if (item.kind === "moduleDescription") return "Module Description";
  if (item.kind === "recommendation") return "Recommendation";

  const itemTypeMeta = item.meta?.find((metaEntry) => metaEntry.kind === "itemType");
  if (!itemTypeMeta || itemTypeMeta.kind !== "itemType") {
    return item.kind === "suggestedContent" ? "Suggested Content" : "Activity";
  }

  return (
    COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemTypeMeta.itemType] ??
    (item.kind === "suggestedContent" ? "Suggested Content" : "Activity")
  );
}

interface ReviewPanelProps {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  selectedItemId: string | null;
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  selectedDecision: CourseEvaluatorReviewDecision;
  filterMode: "all" | "undecided";
  collapsedSectionIds: Set<string>;
  reviewedProgress: number;
  getDecision: (id: string) => CourseEvaluatorReviewDecision;
  lastSavedAt?: string | null;
  isSaving?: boolean;
  onExitReview: () => void;
  onSelectItem: (itemId: string | null) => void;
  onToggleSection: (sectionId: string) => void;
  onFilterModeChange: (mode: "all" | "undecided") => void;
  onStartReviewing: () => void;
  onAddModule?: () => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onRejectGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onUndo?: () => void;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFields) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
  // Publish flow
  queuedPublishItems: QueuedPublishItem[];
  lastPublishedItems: PublishedPublishItem[];
  publishManualSteps: PublishManualStep[];
  publishDocumentUpdates: PublishDocumentUpdate[];
  undecidedCount: number;
  isPublishSheetOpen: boolean;
  publishStatus: PublishStatus;
  onOpenPublishSheet: () => void;
  onClosePublishSheet: () => void;
  onDonePublish: () => void;
  onPublish: () => void;
  onExportPdf: () => void;
  onExportImscc: () => void;
  isExportingPdf?: boolean;
  isExportingImscc?: boolean;
  publishHistoryRecords: PublishHistoryRecord[];
  pendingManualStepsCount: number;
  isPublishHistoryOpen: boolean;
  isClearingPublishHistory: boolean;
  onOpenPublishHistory: () => void;
  onClosePublishHistory: () => void;
  onClearPublishHistory: () => void;
  canAcceptSelectedItem?: boolean;
  acceptDisabledReason?: string;
  acceptHiddenReason?: string;
}

function ReviewPanel({
  review,
  activityDetails,
  selectedItemId,
  selectedReviewItem,
  selectedDecision,
  filterMode,
  collapsedSectionIds,
  reviewedProgress,
  getDecision,
  lastSavedAt,
  isSaving,
  onExitReview,
  onSelectItem,
  onToggleSection,
  onFilterModeChange,
  onStartReviewing,
  onAddModule,
  onAccept,
  onReject,
  onAcceptGroup,
  onRejectGroup,
  onUndo,
  onSaveEdit,
  onCreateIle,
  onNavigateNext,
  onNavigatePrev,
  onJumpToEntity,
  queuedPublishItems,
  lastPublishedItems,
  publishManualSteps,
  publishDocumentUpdates,
  undecidedCount,
  isPublishSheetOpen,
  publishStatus,
  onOpenPublishSheet,
  onClosePublishSheet,
  onDonePublish,
  onPublish,
  onExportPdf,
  onExportImscc,
  isExportingPdf = false,
  isExportingImscc = false,
  publishHistoryRecords,
  pendingManualStepsCount,
  isPublishHistoryOpen,
  isClearingPublishHistory,
  onOpenPublishHistory,
  onClosePublishHistory,
  onClearPublishHistory,
  canAcceptSelectedItem,
  acceptDisabledReason,
  acceptHiddenReason,
}: ReviewPanelProps) {
  const isSelectedItemPublished = selectedDecision === "synced";
  const [isOverviewOverlayVisible, setIsOverviewOverlayVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((previous) => !previous);
  }, []);

  useEffect(() => {
    if (!selectedReviewItem) {
      setIsExpanded(false);
    }
  }, [selectedReviewItem]);

  const orderedItems = useMemo(() => review.sidebarGroups.flatMap((group) => group.items), [review.sidebarGroups]);

  const selectedItemPosition = useMemo(() => {
    if (!selectedReviewItem) return -1;
    return orderedItems.findIndex((item) => item.id === selectedReviewItem.id);
  }, [orderedItems, selectedReviewItem]);

  const selectedActivityDetail = selectedReviewItem ? activityDetails[selectedReviewItem.id] : null;

  // Keyboard navigation: ArrowLeft/ArrowRight to switch between items
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNavigateNext?.();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        onNavigatePrev?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onNavigateNext, onNavigatePrev]);

  return (
    <View style={styles.page}>
      {!isExpanded && (
        <ReviewTopBar
          review={review}
          reviewedProgress={reviewedProgress}
          lastSavedAt={lastSavedAt}
          isSaving={isSaving}
          publishHistoryCount={publishHistoryRecords.length}
          pendingManualStepsCount={pendingManualStepsCount}
          onExitReview={onExitReview}
          onOpenPublishSheet={onOpenPublishSheet}
          onOpenPublishHistory={onOpenPublishHistory}
        />
      )}

      <View style={styles.workspace}>
        {!isExpanded && (
          <ReviewSidebar
            review={review}
            selectedItemId={selectedItemId}
            filterMode={filterMode}
            collapsedSectionIds={collapsedSectionIds}
            getDecision={getDecision}
            onSelectItem={onSelectItem}
            onToggleSection={onToggleSection}
            onFilterModeChange={onFilterModeChange}
            onAddModule={onAddModule}
            onAcceptGroup={onAcceptGroup}
            onRejectGroup={onRejectGroup}
          />
        )}

        <View style={styles.contentColumn}>
          {isExpanded && selectedReviewItem && (
            <ExpandedReviewContextBar
              itemTypeLabel={getExpandedItemTypeLabel(selectedReviewItem)}
              courseName={review.courseName}
              moduleActivityThread={selectedActivityDetail?.documentReference ?? selectedReviewItem.title}
              activityTitle={selectedReviewItem.title}
              positionIndex={selectedItemPosition + 1}
              totalCount={orderedItems.length}
              whyFindings={selectedActivityDetail?.issueFindingsDetailed}
              whyRubricCode={selectedActivityDetail?.rubricCode}
              whyRubricTitle={selectedActivityDetail?.rubricTitle}
              whyRubricDescription={selectedActivityDetail?.rubricDescription}
              whyLearningObjective={selectedActivityDetail?.learningObjective}
              onCollapse={() => setIsExpanded(false)}
            />
          )}

          <ReviewActionPanel
            selectedReviewItem={selectedReviewItem}
            decision={selectedDecision}
            onAccept={onAccept}
            onReject={onReject}
            onUndo={onUndo}
            onNavigateNext={onNavigateNext}
            onNavigatePrev={onNavigatePrev}
            canAcceptSelectedItem={canAcceptSelectedItem}
            acceptDisabledReason={acceptDisabledReason}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            acceptHiddenReason={acceptHiddenReason}
          />

          <ScrollView
            style={styles.contentScroll}
            contentContainerStyle={[styles.contentScrollContent, isExpanded && styles.contentScrollContentExpanded]}
            scrollEnabled={!isOverviewOverlayVisible}
            tabIndex={0}
            showsVerticalScrollIndicator>
            <View style={[styles.contentInner, isExpanded && styles.contentInnerExpanded]}>
              <ReviewContentPanel
                review={review}
                selectedReviewItem={selectedReviewItem}
                activityDetails={activityDetails}
                publishManualSteps={publishManualSteps}
                isExpanded={isExpanded}
                isEditDisabled={isSelectedItemPublished}
                onStartReviewing={onStartReviewing}
                onOverlayVisibilityChange={setIsOverviewOverlayVisible}
                onSaveEdit={onSaveEdit}
                onCreateIle={onCreateIle}
                onAccept={onAccept}
                onReject={onReject}
                selectedDecision={selectedDecision}
                canAcceptSelectedItem={canAcceptSelectedItem}
                acceptDisabledReason={acceptDisabledReason}
                onAcceptWithoutNav={onAccept}
                onRejectWithoutNav={onReject}
                onNavigateNext={onNavigateNext}
                onNavigatePrev={onNavigatePrev}
                onJumpToEntity={onJumpToEntity}
              />
            </View>
          </ScrollView>
        </View>
      </View>

      <PublishSheet
        open={isPublishSheetOpen}
        items={queuedPublishItems}
        publishedItems={lastPublishedItems}
        manualSteps={publishManualSteps}
        documentUpdates={publishDocumentUpdates}
        undecidedCount={undecidedCount}
        lmsLabel={review.sourceLabel}
        canPublishToLms={review.canPublishToLms}
        status={publishStatus}
        onClose={onClosePublishSheet}
        onDone={onDonePublish}
        onPublish={onPublish}
        onExportPdf={onExportPdf}
        onExportImscc={onExportImscc}
        isExportingPdf={isExportingPdf}
        isExportingImscc={isExportingImscc}
      />
      <PublishHistorySheet
        open={isPublishHistoryOpen}
        records={publishHistoryRecords}
        lmsLabel={review.sourceLabel}
        isClearing={isClearingPublishHistory}
        onClose={onClosePublishHistory}
        onClearHistory={onClearPublishHistory}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    ...(Platform.OS === "web"
      ? ({
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
        } as unknown as ViewStyle)
      : {}),
  },
  workspace: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: colors.grey[50],
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({
          display: "flex",
          overflow: "hidden",
        } as const)
      : {}),
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
    backgroundColor: colors.grey[50],
  },
  contentScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "scroll",
  },
  contentScrollContent: {
    maxWidth: 1024,
    alignSelf: "center",
    width: "100%",
    padding: 24,
    flexGrow: 1,
  },
  contentScrollContentExpanded: {
    maxWidth: "100%",
    alignSelf: "stretch",
    padding: 0,
  },
  contentInner: {
    width: "100%",
    gap: 16,
  },
  contentInnerExpanded: {
    flex: 1,
    minHeight: 0,
  },
});

export default ReviewPanel;
